import { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { omitBy, isNil, isBoolean } from 'lodash';
import message from 'antd/es/message';

import { invalidFollowList } from '@/components';
import {
  request,
  queryClient,
  login,
  getIsOfficialAccountById,
  OFFICIAL_ACCOUNT_HI_ID_PREFIX,
  convertOfficialHiIdToOriginalId,
  toastError,
  isOfficalAddress,
} from '@/utils';
import { useCurrentAddressInfo, useHiUserCache } from '@/hooks';
import { setQueryFeedAllUsersInList } from '@/views/Stream/hooks/list';
import produce from 'immer';

export enum OfficialAccountVerifyStatus {
  VERIFYING = 1,
  INACTIVE = 2,
  VERIFIED = 3,
  UNVERIFIED = 4,
  REJECT = 5,
}

export const fetchOfficialAccount = (officialId: string) => {
  const params = { id: officialId };
  return request.get<{ official: OfficialAccount }>('/official', params);
};
export const useOfficialAccount = (
  id?: string | number,
  options?: { onError?: () => void; useSlug?: boolean; enabled?: boolean },
) => {
  const officialId = String(id);
  const { address } = useCurrentAddressInfo();
  const { userDict, updateHiUserCache } = useHiUserCache();

  const _enabled = useMemo(() => {
    const defalutEnable = !!officialId && typeof officialId === 'string';
    const optionEnable = isBoolean(options?.enabled) ? options?.enabled : true;
    if (!options?.useSlug) {
      return defalutEnable && optionEnable && isOfficalAddress(id);
    } else {
      return defalutEnable && optionEnable;
    }
  }, [id, officialId, options?.enabled, options?.useSlug]);

  let params: { slug: string } | { id: string };

  if (options?.useSlug) {
    params = { slug: officialId };
  } else {
    params = { id: officialId };
  }

  const { data, ...query } = useQuery(
    ['/official', officialId, address],
    async () => {
      return request.get<{ official: OfficialAccount }>('/official', params);
    },
    {
      enabled: _enabled,
      cacheTime: 1000,
      onError(e: any) {
        console.error('fetch official account detail failed', e);
        options?.onError?.();
      },
      onSuccess(res) {
        if (getIsOfficialAccountById(officialId) && res?.official) {
          updateHiUserCache({
            [hiUserId]: res?.official,
          });
        }
      },
    },
  );

  const hiUserId = `${OFFICIAL_ACCOUNT_HI_ID_PREFIX}${officialId}`;

  const detail = data?.official || (userDict[hiUserId] as OfficialAccount);

  return {
    officialAccountDetail: detail,
    unverified: detail?.verify_status !== OfficialAccountVerifyStatus.VERIFIED,
    ...query,
  };
};

export interface OfficialFollowRes {
  is_success: boolean;
}

export const useOfficialFollow = (officialId: string | number) => {
  return useMutation(
    ({
      isFollowing,
      ...rest
    }: {
      isFollowing: boolean;
      [key: string]: string | number | boolean;
    }) => {
      return request.post<OfficialFollowRes>(`/official/${isFollowing ? 'unfollow' : 'follow'}`, {
        id: Number(convertOfficialHiIdToOriginalId(String(officialId))),
        ...omitBy(rest, isNil),
      });
    },
  );
};

export const useUpdateOfficialFollow = (
  officialId: string | number,
  followCallback?: (isFollowing: boolean) => void,
) => {
  const id = String(officialId);
  const { officialAccountDetail } = useOfficialAccount(id);
  const isFollowing = officialAccountDetail?.is_following ?? false;
  const [refetchLoading, setRefetchLoading] = useState(false);
  const { isVerified, address } = useCurrentAddressInfo();

  const { mutate, isLoading } = useOfficialFollow(officialId);

  const updateFollow = useCallback(
    (restParams?: Record<string, string | number | boolean | undefined>) => {
      if (!isVerified) {
        login();

        return;
      }

      mutate(
        { isFollowing, ...restParams },
        {
          onError(err) {
            console.error('update follow failed', err);
            toastError(err || 'failed');
          },
          async onSuccess(data) {
            if (data.is_success) {
              try {
                setRefetchLoading(true);
                queryClient.invalidateQueries(['/official', id, address]);
                queryClient.invalidateQueries(['/official', officialAccountDetail?.slug, address]);
                invalidFollowList('official', id);

                const followUserInFeedHandler = (pre?: UnionAccount) => {
                  if (pre) {
                    pre.is_following = !pre.is_following;
                  }
                };

                address && setQueryFeedAllUsersInList(address, id, followUserInFeedHandler);
                followCallback && followCallback(!isFollowing);
              } catch (e) {
                console.error('update follow failed', e);
              } finally {
                setRefetchLoading(false);
              }
            } else {
              message.error('Error');
            }
          },
        },
      );
    },
    [isVerified, mutate, isFollowing, id, address, officialAccountDetail?.slug, followCallback],
  );

  return {
    updateFollow,
    isLoading: isLoading || refetchLoading,
  };
};

export const setOfficialDetail = ({
  currentId,
  officialId,
  recipe,
}: {
  currentId: string;
  officialId: string;
  recipe: (userDetail: OfficialAccount) => void;
}) => {
  const queryKey = ['/official', officialId, currentId];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<{ official: OfficialAccount } | undefined>(queryKey, pre => {
      if (!pre) {
        return;
      }
      return produce(pre, draft => recipe(draft.official));
    });
  }
};
